import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, UserPermissions } from '../types';
import authService from '../services/authService';

interface AuthContextType {
  user: User | null;
  permissions: UserPermissions | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clear error helper
  const clearError = useCallback(() => setError(null), []);

  // Refresh user data and permissions
  const refreshUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // First check auth status
      const authStatus = await authService.getAuthStatus();
      
      if (authStatus.authenticated && authStatus.user) {
        setUser(authStatus.user);
        
        // Get detailed permissions
        try {
          const userPermissions = await authService.getUserPermissions();
          setPermissions(userPermissions);
        } catch (permError) {
          console.warn('Failed to load permissions:', permError);
          // Don't fail completely if permissions fail
        }
      } else {
        setUser(null);
        setPermissions(null);
      }
    } catch (err) {
      console.warn('Auth check failed:', err);
      setUser(null);
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Login function
  const login = useCallback(async (username: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const loginResponse = await authService.login({ username, password });
      setUser(loginResponse.user);
      
      // Get permissions after successful login
      try {
        const userPermissions = await authService.getUserPermissions();
        setPermissions(userPermissions);
      } catch (permError) {
        console.warn('Failed to load permissions after login:', permError);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      setUser(null);
      setPermissions(null);
      throw err; // Re-throw so login component can handle it
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await authService.logout();
    } catch (err) {
      console.warn('Logout API call failed:', err);
      // Continue with local logout even if API fails
    } finally {
      setUser(null);
      setPermissions(null);
      setIsLoading(false);
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Context value
  const value = {
    user,
    permissions,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    refreshUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper hooks for role-based access
export const usePermissions = () => {
  const { permissions } = useAuth();
  return permissions;
};

export const useRoleCheck = () => {
  const { user, permissions } = useAuth();
  
  const hasRole = useCallback((role: string) => {
    return user?.role === role;
  }, [user]);
  
  const canCreatePermits = useCallback(() => {
    return user?.can_create_permits || false;
  }, [user]);
  
  const canReceivePermits = useCallback((permitType?: string) => {
    if (!user || !permissions) return false;
    
    if (!permitType) {
      return user.can_receive_permits;
    }
    
    // Check specific permit type
    const typePermissions = permissions.permissions.permit_types[permitType as keyof typeof permissions.permissions.permit_types];
    return typePermissions?.can_receive || false;
  }, [user, permissions]);
  
  const canIssuePermits = useCallback(() => {
    return user?.can_issue_permits || false;
  }, [user]);
  
  const canSuperviseWork = useCallback(() => {
    return user?.can_supervise_work || false;
  }, [user]);
  
  const hasUIAccess = useCallback((feature: string) => {
    if (!permissions) return false;
    return permissions.permissions.ui_access[feature as keyof typeof permissions.permissions.ui_access] || false;
  }, [permissions]);

  // Viewer-specific permission checks
  const isViewerOnly = useCallback(() => {
    return user?.role === 'viewer';
  }, [user]);

  const canViewDashboard = useCallback(() => {
    return user?.can_view_dashboard || false;
  }, [user]);

  const canViewSCADA = useCallback(() => {
    return user?.can_view_scada || false;
  }, [user]);

  const canViewAlarms = useCallback(() => {
    return user?.can_view_alarms || false;
  }, [user]);

  const canViewCameras = useCallback(() => {
    return user?.can_view_cameras || false;
  }, [user]);

  const canViewPlannedWork = useCallback(() => {
    return user?.can_view_planned_work || false;
  }, [user]);

  const canViewAllPermits = useCallback(() => {
    return user?.can_view_all_permits || false;
  }, [user]);

  const canWriteSCADATags = useCallback(() => {
    return user?.can_write_scada_tags || false;
  }, [user]);

  const canClearAlarms = useCallback(() => {
    return user?.can_clear_alarms || false;
  }, [user]);

  const canManageSiteData = useCallback(() => {
    return user?.can_manage_site_data || false;
  }, [user]);

  const canAccessReports = useCallback(() => {
    return user?.can_access_reports || false;
  }, [user]);

  const canManageReports = useCallback(() => {
    return user?.can_manage_reports || false;
  }, [user]);

  return {
    hasRole,
    canCreatePermits,
    canReceivePermits,
    canIssuePermits,
    canSuperviseWork,
    hasUIAccess,
    // Viewer-specific checks
    isViewerOnly,
    canViewDashboard,
    canViewSCADA,
    canViewAlarms,
    canViewCameras,
    canViewPlannedWork,
    canViewAllPermits,
    canWriteSCADATags,
    canClearAlarms,
    canManageSiteData,
    // Reporting permissions
    canAccessReports,
    canManageReports,
  };
}; 